import { eventSchema } from '../Event/schemas';
import { formSchema } from '../Form/schemas';
import { createListApiSchema } from '../General/schemas';
import { groupSchema } from '../Group/schemas';
import { userSchema } from '../User/schemas';
import z from 'zod';

export const taskTemplateSchema = z
	.object({
		id: z.string(),
		slug: z.string(),
		name: z.string().optional(),
		description: z.string().optional(),
		icon: z.string().optional(),
		type: z.string(),
		form: z.string().optional(),
		formSpecs: z.unknown().optional(),
		estMinutes: z.number().optional(),
		validDuration: z.string().optional().nullable(),
		scope: z.enum(['*', 'userOnly', 'adminOnly', 'kioskOnly', 'selectedRolesOnly']),
		selectedRoleIds: z.array(z.string()).optional(),
		isArchived: z.boolean(),
		isFree: z.boolean(),
		keywords: z.string().optional(),
		rolesCount: z.number().optional(),
		typesCount: z.number().optional(),
		sourceTemplateId: z.string().optional().nullable(),
		sourceTemplateVersion: z.string().optional().nullable(),
	})
	.describe('TaskTemplate');

export const baseTaskSchema = z
	.object({
		id: z.string(),
		createdAt: z.coerce.date(),
		userId: z.string().optional(),
		eventId: z.string().optional(),
		groupId: z.string().optional(),
		status: z.enum([
			'locked',
			'todo',
			'inProgress',
			'pending',
			'done',
			'expiryWarning',
			'superseded',
			'expired',
			'failed',
		]),
		templateSlug: z.string(),
		name: z.string(),
		type: z.string(),
		form: z.string(),
		estMinutes: z.number().optional(),
		description: z.string().optional(),
		icon: z.string().optional(),
		result: z.any(),
		documentIds: z.array(z.string()).optional(),
		validFrom: z.coerce.date().optional(),
		validToOverride: z.coerce.date().optional(),
		validTo: z.coerce.date().optional(),
		scope: z.enum(['*', 'userOnly', 'adminOnly', 'kioskOnly', 'selectedRolesOnly']),
		superseeds: z.string().optional(),
		isMigrationTask: z.boolean().optional(),
		isDraft: z.boolean().optional(),
		Form: z.lazy(() => formSchema).optional(),
		user: z.lazy(() => userSchema).optional(),
		group: z
			.lazy(() => groupSchema)
			.optional()
			.nullable(),
		event: z
			.lazy(() => eventSchema)
			.optional()
			.nullable(),
		executePermission: z.object({
			isAllowed: z.boolean(),
			denyReason: z.string().optional(),
		}),
	})
	.describe('BaseTask');

export type BaseTask = z.infer<typeof baseTaskSchema>;

export const taskSchema = baseTaskSchema
	.extend({
		...baseTaskSchema.shape,
		template: z
			.lazy(() => taskTemplateSchema)
			.optional()
			.nullable(),
		completedBefore: z.coerce.date().optional(),
	})
	.describe('Task');

export const taskListSchema = createListApiSchema(taskSchema).describe('TaskList');

export const taskTemplateListSchema = createListApiSchema(taskTemplateSchema).describe('TaskTemplateList');

export const taskWithVirtualsSchema = taskSchema.extend({
	template: z
		.lazy(() => taskTemplateSchema)
		.optional()
		.nullable(),
	user: z
		.lazy(() => userSchema)
		.optional()
		.nullable(),
	group: z
		.lazy(() => groupSchema)
		.optional()
		.nullable(),
	event: z
		.lazy(() => eventSchema)
		.optional()
		.nullable(),
});

export const preAccessTasksResponseSchema = z
	.object({
		tasks: z.array(taskSchema),
		hasInaccessibleTasks: z.boolean(),
	})
	.describe('PreAccessTasksResponse');
